/**
 * API Configuration for anim8pro
 * 
 * CRITICAL: This module determines the backend API URL dynamically at runtime.
 * This solves the issue where stale environment variables from old builds
 * could cause requests to go to the wrong backend.
 * 
 * In production, the frontend and backend are served from the same domain,
 * so we use window.location.origin as the base URL.
 */

/**
 * Get the base API URL
 * - In production (app.anim8pro.com): Uses same-origin (no CORS issues)
 * - In development (localhost): Uses REACT_APP_BACKEND_URL from .env
 */
export function getApiBaseUrl() {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }

  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  // Production domains - use same-origin to avoid CORS
  const productionDomains = [
    'app.anim8pro.com',
    'www.app.anim8pro.com',
    'anim8pro.com',
    'www.anim8pro.com',
  ];
  
  if (productionDomains.includes(hostname)) {
    // In production, API is on the same domain
    return origin;
  }
  
  // For Emergent preview/deployment environments
  // These domains end with .emergent.host or .emergentagent.com
  if (hostname.endsWith('.emergent.host') || hostname.endsWith('.emergentagent.com')) {
    // Use same-origin for preview environments too
    return origin;
  }
  
  // Development - use environment variable
  // localhost:3000 frontend needs to call localhost:8001 backend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_BACKEND_URL || origin;
}

/**
 * Get the full API URL with /api prefix
 */
export function getApiUrl() {
  return `${getApiBaseUrl()}/api`;
}

// Export constants for backward compatibility
export const API_BASE = getApiBaseUrl();
export const API = getApiUrl();

// Debug logging in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('[API Config] Base URL:', API_BASE);
  console.log('[API Config] API URL:', API);
  console.log('[API Config] Hostname:', window.location.hostname);
}
